/**
 *
 * CampaignTemplates/Selector
 *
 */

import React, { memo, useMemo } from 'react'

// NPM Libraries
import { useHistory } from 'react-router-dom'

// GraphQL
import useQuery from 'components/UseQuery'
import { gql } from '@apollo/client'

// Components
import CampaignTemplatesShow from 'components/CampaignTemplates/Show'
import Gallery from 'components/Gallery'
import { Campaign } from 'globalVars'

export const GET_CAMPAIGN_TEMPLATES = gql`
	query getTemplates($always_on: Boolean) {
		campaign_templates(always_on: $always_on) {
			id
			name
			objective
			topic
			topic_to_s
			headline
			description
			description2
			disclaimer_json
			highlights
			questions
			landing_page_background_url
			landing_page_background_id
			campaign_image_url
			is_meal_event
		}
	}
`

function TemplateSelector({ campaign, setAppliedTemplate }) {
	const history = useHistory()
	const {
		data: { campaign_templates },
		loading,
	} = useQuery(GET_CAMPAIGN_TEMPLATES, {
		variables: {
			always_on: campaign.always_on,
		},
		default_data: { campaign_templates: [] },
	})

	/* eslint-disable indent */
	const objective_filter_options = campaign.always_on
		? {
				1: 'Appointments',
				3: 'Download',
				5: 'Webinar',
		  }
		: {
				0: 'Events',
				1: 'Appointments',
				3: 'Download',
				5: 'Webinar',
		  }
	/* eslint-enable indent */

	const confirmAndApplyTemplate = (template) => {
		const already_started = Object.keys(campaign).some(
			(key) => ['headline', 'description'].includes(key) && campaign[key]?.length > 0,
		)

		if (already_started) {
			/* eslint-disable no-undef */
			swal({
				text: 'Some of your campaign information may be changed or erased. Continue?',
				confirmButtonText: 'Yes',
				cancelButtonText: 'Cancel',
				confirmButtonClass: 'btn btn-danger d-inline mr-2',
				cancelButtonClass: 'btn btn-default d-inline',
				buttonsStyling: false,
			}).then(() => setAppliedTemplate(template))
			/* eslint-disable no-undef */
		} else {
			setAppliedTemplate(template)
		}
	}

	const initial_filter_key = String(campaign?.objective || Campaign.OBJECTIVE_EVENTS)

	const templates_prop = useMemo(
		() =>
			campaign_templates.map((template) => ({
				id: template.id,
				filter_key: String(template.objective),
				original: template,
				show: <CampaignTemplatesShow campaign_template={template} />,
				content_below: (
					<div>
						{template.name}
						<br />
						Topic: {template.topic_to_s}
						<br />
						{Campaign.OBJECTIVES[template.objective]}
						<br />
						<button
							type="button"
							onClick={() => confirmAndApplyTemplate(template)}
							className="text-warning btn-unstyled mt-1"
						>
							Select Template
						</button>
					</div>
				),
				visible: template.objective === initial_filter_key,
			})),
		[JSON.stringify(campaign_templates)],
	)

	const goToCampaign = () => history.push(`/campaigns/${campaign.id}#edit`)

	return (
		<Gallery
			title="Choose Template for Your Campaign"
			filter_options={objective_filter_options}
			initial_filter_key={initial_filter_key}
			search_keys={['name', 'headline', 'description', 'topic_to_s']}
			startFromScratch={history.location.pathname.startsWith('/campaign') ? undefined : goToCampaign}
			templates={templates_prop}
			loading={loading}
		/>
	)
}

TemplateSelector.propTypes = {}

export default memo(TemplateSelector)
